import { ref, onBeforeUnmount } from 'vue';

const SSE_EVENTS = {
  USER_MESSAGE: 'user_message',
  TOOL_CALLS: 'tool_calls',
  DELTA: 'delta',
  COMPLETE: 'complete',
  RECOMMENDATIONS: 'recommendations',
  ERROR: 'error',
  HEARTBEAT: 'heartbeat',
};

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY = 1000;

export function useSSE() {
  const eventSource = ref(null);
  const isConnected = ref(false);
  const retryCount = ref(0);
  const currentSSEUrl = ref(null);

  let callbacks = {};

  const connect = (sseUrl, eventCallbacks = {}) => {
    if (eventSource.value) {
      disconnect();
    }

    currentSSEUrl.value = sseUrl;
    callbacks = eventCallbacks;
    retryCount.value = 0;

    establishConnection();
  };

  const establishConnection = () => {
    let sseUrl = currentSSEUrl.value;
    
    if (!sseUrl) {
      console.error('[SSE] No SSE URL provided');
      return;
    }
    
    if (sseUrl.startsWith('/')) {
      sseUrl = window.location.origin + sseUrl;
    }
    
    try {
      eventSource.value = new EventSource(sseUrl, { withCredentials: true });
      
      eventSource.value.onopen = () => {
        isConnected.value = true;
        retryCount.value = 0;
      };

      eventSource.value.onerror = (error) => {
        console.error('[SSE] Connection error:', error);
        isConnected.value = false;
        
        if (eventSource.value?.readyState === EventSource.CLOSED) {
          handleReconnect();
        }
      };
      
      eventSource.value.onmessage = (event) => {
      };

      eventSource.value.addEventListener(SSE_EVENTS.USER_MESSAGE, (event) => {
        handleEvent(SSE_EVENTS.USER_MESSAGE, event);
      });

      eventSource.value.addEventListener(SSE_EVENTS.TOOL_CALLS, (event) => {
        handleEvent(SSE_EVENTS.TOOL_CALLS, event);
      });

      eventSource.value.addEventListener(SSE_EVENTS.DELTA, (event) => {
        handleEvent(SSE_EVENTS.DELTA, event);
      });

      eventSource.value.addEventListener(SSE_EVENTS.COMPLETE, (event) => {
        handleEvent(SSE_EVENTS.COMPLETE, event);
      });

      eventSource.value.addEventListener(SSE_EVENTS.RECOMMENDATIONS, (event) => {
        handleEvent(SSE_EVENTS.RECOMMENDATIONS, event);
      });

      eventSource.value.addEventListener(SSE_EVENTS.ERROR, (event) => {
        handleEvent(SSE_EVENTS.ERROR, event);
      });

      eventSource.value.addEventListener(SSE_EVENTS.HEARTBEAT, (event) => {
        handleEvent(SSE_EVENTS.HEARTBEAT, event);
      });

    } catch (error) {
      console.error('[SSE] Failed to create EventSource:', error);
      handleReconnect();
    }
  };

  const handleEvent = (eventType, event) => {
    try {
      const data = JSON.parse(event.data);
      
      if (callbacks[eventType]) {
        callbacks[eventType](data);
      }

      if (callbacks.onAnyEvent) {
        callbacks.onAnyEvent(eventType, data);
      }
    } catch (error) {
      console.error(`[SSE] Error parsing ${eventType} event:`, error);
    }
  };

  const handleReconnect = () => {
    if (retryCount.value >= MAX_RETRIES) {
      console.error('[SSE] Max retries reached, giving up');
      if (callbacks.onMaxRetriesReached) {
        callbacks.onMaxRetriesReached();
      }
      return;
    }

    const delay = BASE_RETRY_DELAY * Math.pow(2, retryCount.value);
    retryCount.value++;


    setTimeout(() => {
      if (currentSSEUrl.value) {
        establishConnection();
      }
    }, delay);
  };

  const disconnect = () => {
    if (eventSource.value) {
      eventSource.value.close();
      eventSource.value = null;
    }
    isConnected.value = false;
    currentSSEUrl.value = null;
    callbacks = {};
  };

  onBeforeUnmount(() => {
    disconnect();
  });

  return {
    connect,
    disconnect,
    isConnected,
    retryCount,
    SSE_EVENTS,
  };
}

export { SSE_EVENTS };

